'use client'

import { Avatar } from '@repo/ui/Avatar'
import { ClientMatterDetailsMenuItem } from '@repo/ui/ClientMatterDetailsMenuItem'
import { formatDate } from '@repo/utils/time'
import { LoadingState } from '@repo/ui/LoadingState'
import { LockIcon } from '@repo/ui/LockIcon'
import { MatterStatusPill } from '@repo/ui/MatterStatusPill'
import { Pill } from '@repo/ui/Pill'
import { useClientStore } from '@repo/store/useClientStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useNavigate } from 'react-router-dom'
import { useUndertakingsStore } from '@repo/store/useUndertakingsStore'
import { useState, useEffect } from 'react'
import { useUrlStore } from '@repo/store/useUrlStore'
import MUIAlert from '@mui/material/Alert'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import styles from './MatterDrawerMatterTab.module.scss'

const getPrivateOrLegalAidValue = (value: string) => {
  if (!value) return '-'
  if (value === 'P') return 'Private'
  return 'Legal aid'
}

export const MatterDrawerMatterTab = ({ workspaceDetailCardVisible }: MatterDrawerMatterTabProps) => {
  const {
    currentMatter,
    matters,
    fetchMatter,
    currentClientMatterNo,
    matterDrawerIsOpen,
    setMatterDrawerIsOpen,
    matterDrawerClientNo,
    matterDrawerClientMatterNo
  } = useMattersStore()
  const { fetchMatterUndertakingCount, isUndertakingsSupported, undertakingsCounts } = useUndertakingsStore()
  const { clients, fetchClient } = useClientStore()
  const { clientNo, clientMatterNo } = currentMatter || {}
  const [client, setClient] = useState<any>(null)
  const [matter, setMatter] = useState<any>(null)
  const [matterHasOutstandingUndertakings, setMatterHasOutstandingUndertakings] = useState<boolean>(false)
  const [undertakingsDischargeCount, setUndertakingsDischargeCount] = useState(0)
  const [undertakingsDischargeNotPossibleCount, setUndertakingsDischargeNotPossibleCount] = useState(0)
  const [undertakingsOutstandingCount, setUndertakingsOutstandingCount] = useState(0)
  const [undertakingsTotalCount, setUndertakingsTotalCount] = useState(0)
  const [width, setWidth] = useState<string>('')
  const { navigateWithClientMatterNo } = useUrlStore()
  const navigate = useNavigate()

  useEffect(() => {
    // fetch client details from API
    if (matterDrawerIsOpen && matterDrawerClientNo) {
      fetchClient({ clientNo: matterDrawerClientNo })
    }

    if (!matterDrawerIsOpen && workspaceDetailCardVisible && !matterDrawerClientNo && clientNo) {
      fetchClient({ clientNo: clientNo })
    }
  }, [matterDrawerIsOpen, matterDrawerClientNo, clientNo, workspaceDetailCardVisible])

  useEffect(() => {
    // set client state
    if (matterDrawerClientNo && clients?.[matterDrawerClientNo]) {
      setClient(clients[matterDrawerClientNo])
    }

    if (!matterDrawerClientNo && workspaceDetailCardVisible && clients?.[clientNo]) {
      setClient(clients[clientNo])
    }
  }, [clients, matterDrawerClientNo, clientNo, workspaceDetailCardVisible])

  useEffect(() => {
    // fetch matter details from API
    if (matterDrawerIsOpen && matterDrawerClientMatterNo) {
      fetchMatter({ clientMatterNo: matterDrawerClientMatterNo })
    }

    if (!matterDrawerClientMatterNo && !matterDrawerIsOpen && workspaceDetailCardVisible && clientMatterNo) {
      fetchMatter({ clientMatterNo: clientMatterNo })
    }
  }, [matterDrawerIsOpen, matterDrawerClientMatterNo, clientMatterNo, workspaceDetailCardVisible])

  useEffect(() => {
    //set matter state
    if (matterDrawerClientMatterNo && matters?.[matterDrawerClientMatterNo]) {
      setMatter(matters[matterDrawerClientMatterNo])
    }

    if (!matterDrawerClientMatterNo && workspaceDetailCardVisible && matters?.[clientMatterNo]) {
      setMatter(matters[clientMatterNo])
    }
  }, [matters, matterDrawerClientMatterNo, clientMatterNo, workspaceDetailCardVisible])

  useEffect(() => {
    const updateWidth = () => {
      setWidth(window.innerWidth < 1200 ? '100%' : 'calc(50% - 12px)')
    }

    updateWidth() // Set initial width
    window.addEventListener('resize', updateWidth)

    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  useEffect(() => {
    if (!isUndertakingsSupported || !clientNo || !matter || (!matterDrawerIsOpen && !workspaceDetailCardVisible)) return

    fetchMatterUndertakingCount({
      clientNo: clientNo,
      matterNo: matter.matterNo
    })
  }, [fetchMatterUndertakingCount, isUndertakingsSupported, clientNo, matter])

  useEffect(() => {
    if (matter && undertakingsCounts) {
      const matchingCount = undertakingsCounts.find(
        (count: any) => count.clientNo === clientNo && count.matterNo === matter.matterNo
      )

      if (matchingCount) {
        setMatterHasOutstandingUndertakings(matchingCount.matterHasOutstandingUndertakings)
        setUndertakingsDischargeCount(matchingCount.dischargedCount || 0)
        setUndertakingsDischargeNotPossibleCount(matchingCount.dischargeNotPossibleCount || 0)
        setUndertakingsOutstandingCount(matchingCount.outstandingCount || 0)
        setUndertakingsTotalCount(matchingCount.totalCount || 0)
      } else {
        setMatterHasOutstandingUndertakings(false)
        setUndertakingsDischargeCount(0)
        setUndertakingsDischargeNotPossibleCount(0)
        setUndertakingsOutstandingCount(0)
        setUndertakingsTotalCount(0)
      }
    }
  }, [undertakingsCounts, clientNo, matter])

  const gotoMatterAndNavigate = (e: Event) => {
    e.stopPropagation()

    if (matter.isUserRestricted) return

    const targetClientMatterNo = matterDrawerIsOpen ? matterDrawerClientMatterNo : currentClientMatterNo
    setMatterDrawerIsOpen(false)

    // go to default matter page
    navigateWithClientMatterNo({ clientMatterNo: targetClientMatterNo })
  }

  const goToClientDashboardAndNavigate = (e: Event) => {
    e.stopPropagation()

    if (matter.isUserRestricted) return

    const targetClientMatterNo = matterDrawerIsOpen ? matterDrawerClientMatterNo : currentClientMatterNo
    setMatterDrawerIsOpen(false)
    navigateWithClientMatterNo({
      route: '/case-management/clients/client-workspace',
      clientMatterNo: targetClientMatterNo,
      appendClientMatterNo: true
    })
  }

  if (
    (!workspaceDetailCardVisible && !matterDrawerClientMatterNo) ||
    (workspaceDetailCardVisible && !clientMatterNo) ||
    !matter
  ) {
    return (
      <MUIStack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', height: '300px' }}
        data-testid="MatterDrawerMatterTab"
      >
        <LoadingState />
      </MUIStack>
    )
  }

  const isUserRestricted = matter?.isUserRestricted || false

  if (isUserRestricted) {
    return (
      <MUIStack
        justifyContent="flex-start"
        alignItems="center"
        sx={{ width: '100%', height: '300px' }}
        data-testid="MatterDrawerMatterTab"
      >
        <MUIAlert severity="error" sx={{ m: '30px 16px 16px' }}>
          You are not authorised to view details about this matter
        </MUIAlert>
      </MUIStack>
    )
  }

  const dateOpened = formatDate(matter.dateOpened, 'do MMM yyyy')
  const dateCompleted = formatDate(matter.dateCompleted, 'do MMM yyyy')
  const dateArchived = formatDate(matter.dateArchived, 'do MMM yyyy')

  const hrStyle = { width: '100%', margin: '-4px 0 8px', border: '1px solid rgba(5, 28, 51, 0.06)' }

  return (
    <MUIStack
      data-testid="MatterDrawerMatterTab"
      role="tabpanel"
      className={styles.MatterDrawerMatterTab}
      gap={5}
      sx={{ padding: '24px', width: '100%' }}
    >
      <MUIStack
        className={styles.MatterDrawerMatterTab}
        direction="row"
        justifyContent="space-between"
        sx={{ width: '100%', position: 'relative' }}
        flexWrap="wrap"
        gap={workspaceDetailCardVisible ? 4 : 6}
      >
        <ClientMatterDetailsMenuItem
          title="Client workspace"
          width={width}
          label="Click to open"
          variant="large"
          onClick={isUserRestricted ? null : goToClientDashboardAndNavigate}
          enableCopy={false}
          showHoverIcon={true}
        />
        <ClientMatterDetailsMenuItem
          title="Matter workspace"
          width={width}
          label="Click to open"
          variant="large"
          onClick={isUserRestricted ? null : gotoMatterAndNavigate}
          enableCopy={false}
          showHoverIcon={true}
        />

        <ClientMatterDetailsMenuItem
          title="Client no"
          width={width}
          label={matterDrawerClientNo || clientNo || '-'}
          variant="large"
        />

        <ClientMatterDetailsMenuItem
          title="Matter no"
          width={width}
          label={matterDrawerClientMatterNo || clientMatterNo || '-'}
          variant="large"
        />
        <ClientMatterDetailsMenuItem
          title="Matter description"
          width={width}
          label={matter.matterDescription || '-'}
          variant="large"
          noWrap={false}
        />

        <ClientMatterDetailsMenuItem
          title="Work type"
          width={width}
          variant="large"
          label={!matter.workflowTypeDescription ? '-' : null}
          WorktypeHover={matter.workflowTypeDescription ? matter.workflowTypeDescription : null}
        >
          {matter.workflowTypeDescription ? (
            <MUIStack direction="row" gap={4} sx={{ mt: 1 }}>
              <Pill label={matter.workflowTypeDescription} backgroundColor="background.mid" />
            </MUIStack>
          ) : null}
        </ClientMatterDetailsMenuItem>

        <hr style={hrStyle} />

        <ClientMatterDetailsMenuItem
          title="Status"
          width={width}
          variant="large"
          hoverElement={
            isUndertakingsSupported &&
            undertakingsTotalCount > 0 && (
              <MUIStack
                display="flex"
                direction="column"
                justifyContent="center"
                alignItems="flex-start"
                height="auto"
                width="100%"
                flexGrow="1"
                gap={1}
                sx={{ cursor: 'pointer', p: 2 }}
              >
                <MUITypography
                  variant="h6"
                  sx={{
                    m: 0,
                    p: 0,
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  TOTAL: {undertakingsTotalCount}
                </MUITypography>
                <MUITypography
                  variant="body2"
                  sx={{
                    m: 0,
                    p: 0,
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Discharged: {undertakingsDischargeCount}
                </MUITypography>
                <MUITypography
                  variant="body2"
                  sx={{
                    m: 0,
                    p: 0,
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Outstanding: {undertakingsOutstandingCount}
                </MUITypography>
                <MUITypography
                  variant="body2"
                  sx={{
                    m: 0,
                    p: 0,
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Not possible: {undertakingsDischargeNotPossibleCount}
                </MUITypography>
              </MUIStack>
            )
          }
        >
          <MUIStack direction="row" gap={2} sx={{ mt: 1 }} flexWrap={'wrap'}>
            <MatterStatusPill matter={matter} />
            {client?.highRisk ? <Pill label="High risk" color="white" backgroundColor="error.main" /> : null}
            {matter?.prospect ? <Pill label="Prospect" backgroundColor="support.sand" /> : null}
            {isUserRestricted ? <LockIcon sx={{ width: '18px', height: '18px', mt: '2px' }} /> : null}
            {isUndertakingsSupported && undertakingsTotalCount > 0 ? (
              <MUIButton
                onClick={() => navigate('/case-management/matters/undertakings')}
                variant="text"
                sx={{ minWidth: '0', padding: '0', textTransform: 'unset' }}
              >
                <Pill
                  label={matterHasOutstandingUndertakings ? 'Outstanding undertakings' : 'Undertakings discharged'}
                  backgroundColor={matterHasOutstandingUndertakings ? 'warning.light' : 'success.main'}
                  color="white"
                />
              </MUIButton>
            ) : null}
          </MUIStack>
        </ClientMatterDetailsMenuItem>

        <ClientMatterDetailsMenuItem
          title="Unique file no"
          width={width}
          label={matter.uniqueFileNo || '-'}
          variant="large"
        />

        <hr style={hrStyle} />

        <ClientMatterDetailsMenuItem title="Fee earner" width={width} variant="large">
          <MUIStack direction="row" gap={2} sx={{ mt: 1, mb: 1 }} alignItems="center">
            <Avatar feeEarnerId={matter.feeEarnerId} name={matter.feeEarnerDescription} size={'32px'} />
            <MUITypography variant="body2" sx={{ mb: 0, textAlign: 'left' }}>
              {matter.feeEarnerDescription || '-'}
            </MUITypography>
          </MUIStack>
        </ClientMatterDetailsMenuItem>

        <ClientMatterDetailsMenuItem title="Supervising fee earner" width={width} variant="large">
          <MUIStack direction="row" gap={2} sx={{ mt: 1, mb: 1 }} alignItems="center">
            <Avatar
              feeEarnerId={matter.supervisingFeeEarnerId}
              name={matter.supervisingFeeEarnerDescription}
              size={'32px'}
            />
            <MUITypography variant="body2" sx={{ mb: 0, textAlign: 'left' }}>
              {matter.supervisingFeeEarnerDescription || '-'}
            </MUITypography>
          </MUIStack>
        </ClientMatterDetailsMenuItem>

        <hr style={hrStyle} />

        <ClientMatterDetailsMenuItem
          title="Remuneration type"
          width={width}
          variant="large"
          label={matter.remunerationTypeDescription || '-'}
          noWrap={false}
        />

        <ClientMatterDetailsMenuItem
          title="Private or legal aid"
          width={width}
          label={getPrivateOrLegalAidValue(matter.privateOrLegalAid) || '-'}
          variant="large"
        />

        <hr style={hrStyle} />

        <ClientMatterDetailsMenuItem
          title="Department"
          width={width}
          label={matter.departmentDescription || '-'}
          variant="large"
        />
        <ClientMatterDetailsMenuItem
          title="Branch"
          width={width}
          label={matter.branchDescription || '-'}
          variant="large"
        />

        <hr style={hrStyle} />

        <ClientMatterDetailsMenuItem
          title="Review date"
          width={width}
          label={formatDate(matter.reviewDate) || '-'}
          variant="large"
        />

        <ClientMatterDetailsMenuItem
          title="Review type"
          width={width}
          label={matter.reviewTypeDescription || '-'}
          variant="large"
        />

        <ClientMatterDetailsMenuItem
          title="Review period"
          width={width}
          label={matter.reviewPeriod || '-'}
          variant="large"
        />

        <ClientMatterDetailsMenuItem
          title="Ledger LMD"
          width={width}
          label={formatDate(matter.lastMovementDate) || '-'}
          variant="large"
        />

        <ClientMatterDetailsMenuItem
          title="Time LMD"
          width={width}
          label={formatDate(matter.lastMovementTime) || '-'}
          variant="large"
        />

        <hr style={hrStyle} />

        <ClientMatterDetailsMenuItem title="Date opened" width={width} label={dateOpened || '-'} variant="large" />

        <ClientMatterDetailsMenuItem
          title="Date completed"
          width={width}
          label={dateCompleted || '-'}
          variant="large"
        />

        <ClientMatterDetailsMenuItem title="Date archived" width={width} label={dateArchived || '-'} variant="large" />

        <ClientMatterDetailsMenuItem
          title="Archive location"
          width={width}
          label={matter.archiveLocation || '-'}
          variant="large"
        />
        <ClientMatterDetailsMenuItem
          title="Archive reference"
          width={width}
          label={matter.archiveReference || '-'}
          variant="large"
        />
      </MUIStack>
    </MUIStack>
  )
}

type MatterDrawerMatterTabProps = {
  workspaceDetailCardVisible: boolean
}

MatterDrawerMatterTab.propTypes = {
  workspaceDetailCardVisible: PropTypes.bool
}
